import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { InstructorResponse } from "../contracts/InstructorResponse";
import { UpdateInstructorLanguageRequest } from "../contracts/UpdateInstructorLanguageRequest";
import { UpdateInstructorLicenceNumberRequest } from "../contracts/UpdateInstructorLicenceNumberRequest";
import { UpdateInstructorStatusRequest } from "../contracts/UpdateInstructorStatusRequest";
import { UpdateInstructorOrganizationRoleRequest } from "../contracts/UpdateInstructorOrganizationRoleRequest";
import { InstructorStatus } from "../enums/InstructorStatus";
import { OrganizationRole } from "../enums/OrganizationRole";
import { Instructor } from "../models/Instructor";
import { InstructorResponseMapper } from "../mappers/InstructorResponseMapper";

const mapper = new InstructorResponseMapper();

/**
 * Fetches every instructor in the signed-in instructor's organization
 * (including the viewer themselves and deactivated colleagues).
 */
export const fetchColleagues = createAsyncThunk<
  Instructor[],
  void,
  { rejectValue: ApiErrorResponse }
>("instructors/fetchColleagues", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<InstructorResponse[]>(
      "/instructors/colleagues"
    );
    return mapper.toInstructors(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load colleagues"));
  }
});

/**
 * Persists the signed-in instructor's app display language to their profile,
 * so backend-rendered emails (and new members' default language) follow it.
 */
export const updateInstructorLanguage = createAsyncThunk<
  Instructor,
  string,
  { rejectValue: ApiErrorResponse }
>("instructors/updateInstructorLanguage", async (languageCode, { rejectWithValue }) => {
  try {
    const request: UpdateInstructorLanguageRequest = { languageCode };
    const response = await axiosInstance.put<InstructorResponse>(
      "/instructors/language",
      request
    );
    return mapper.toInstructor(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to update the language"));
  }
});

/** Loads the signed-in instructor's own profile (for fields only they edit, like the licence number). */
export const fetchCurrentInstructor = createAsyncThunk<
  Instructor,
  string,
  { rejectValue: ApiErrorResponse }
>("instructors/fetchCurrentInstructor", async (instructorId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<InstructorResponse>(`/instructors/${instructorId}`);
    return mapper.toInstructor(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load your profile"));
  }
});

/**
 * Sets or clears the signed-in instructor's licence number ("FL-Nr."), printed
 * on training records. A 400 carries the server's validation message.
 */
export const updateInstructorLicenceNumber = createAsyncThunk<
  Instructor,
  string | null,
  { rejectValue: ApiErrorResponse }
>("instructors/updateInstructorLicenceNumber", async (licenceNumber, { rejectWithValue }) => {
  try {
    const request: UpdateInstructorLicenceNumberRequest = { licenceNumber };
    const response = await axiosInstance.put<InstructorResponse>("/instructors/licence-number", request);
    return mapper.toInstructor(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to save the licence number"));
  }
});

/**
 * Activates or deactivates a colleague (Owner/Admin only; never the Owner).
 */
export const updateInstructorStatus = createAsyncThunk<
  Instructor,
  { instructorId: string; status: InstructorStatus },
  { rejectValue: ApiErrorResponse }
>(
  "instructors/updateInstructorStatus",
  async ({ instructorId, status }, { rejectWithValue }) => {
    try {
      const request: UpdateInstructorStatusRequest = { status };
      const response = await axiosInstance.patch<InstructorResponse>(
        `/instructors/${instructorId}/status`,
        request
      );
      return mapper.toInstructor(response.data);
    } catch (error: unknown) {
      return rejectWithValue(
        toApiError(error, "Failed to update the instructor's status")
      );
    }
  }
);

/**
 * Changes a colleague's organization role between Admin and Instructor
 * (Owner only; the Owner's own role can't be changed here).
 */
export const updateInstructorOrganizationRole = createAsyncThunk<
  Instructor,
  { instructorId: string; role: OrganizationRole },
  { rejectValue: ApiErrorResponse }
>(
  "instructors/updateInstructorOrganizationRole",
  async ({ instructorId, role }, { rejectWithValue }) => {
    try {
      const request: UpdateInstructorOrganizationRoleRequest = { role };
      const response = await axiosInstance.patch<InstructorResponse>(
        `/instructors/${instructorId}/organization-role`,
        request
      );
      return mapper.toInstructor(response.data);
    } catch (error: unknown) {
      return rejectWithValue(
        toApiError(error, "Failed to update the instructor's role")
      );
    }
  }
);
